import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Collapse from '@mui/material/Collapse';
import Grow from '@mui/material/Grow';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import sortBy from 'lodash/fp/sortBy';
import { useEffect, useRef, useState } from 'react';
import Avatar from 'react-avatar';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link as RouterLink, matchPath, useLocation, useMatch } from 'react-router-dom';
import { useConfig } from '../config';
import { Organization, OrganizationFeature, OrganizationMigrationStatus, UsagePlan } from '../models';
import { resolvePath, sso as ssoPath, useNavigate, useQuery } from '../routes';
import {
  fromAuth,
  fromOrganizationFeatureFlag,
  fromOrganizations,
  fromSubscriptions,
  fromTestOpsPlatformSubscriptions,
  fromTestOpsProject,
  fromTestResultCount,
  useAppDispatch,
} from '../store';
import { ReactComponent as AddOrganizationIcon } from './icons/AddOrganization.svg';
import { ReactComponent as ArrowDownIcon } from './icons/ArrowDown.svg';
import {
  accountHome,
  accountSetting,
  accountSubscriptionManagement,
  paymentMethod,
  paymentSuccess,
  testCloudCheckout,
  testCloudSubscriptionManagement,
  testOpsPlatformCheckout,
  testOpsPlatformPaymentStatus,
  securitySettings,
  organizationManagement,
  projectManagement,
  maintenanceProcess,
  projectList,
} from './routes';
import { useLaunchDarkly } from '../launchdarkly';

const useStyles = makeStyles(theme => ({
  chevron: {
    lineHeight: 0,
    borderRadius: theme.spacing(0.4),
    color: '#172B4D',
    '&:hover': {
      backgroundColor: '#E8EBED',
      color: '#1F58C1',
    },
  },
  orgName: {
    padding: theme.spacing(0, 1.25),
    color: '#F7F7F9',
    fontSize: theme.spacing(2),
    fontWeight: 500,
    alignSelf: 'center',
    whiteSpace: 'nowrap',
    maxWidth: theme.spacing(32),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  orgList: {
    paddingLeft: theme.spacing(1.5),
  },
  orgItem: {
    padding: theme.spacing(0, 2, 0, 0),
    marginBottom: theme.spacing(1.3),
    fontWeight: 500,
    '&:hover': {
      backgroundColor: '#FAFAFA',
    },
  },
  selectedMarker: {
    width: theme.spacing(0.525),
    height: theme.spacing(4.9),
    borderTopRightRadius: theme.spacing(1.75),
    borderBottomRightRadius: theme.spacing(1.75),
    backgroundColor: '#5294FF',
  },
  invisible: {
    visibility: 'hidden',
  },
  itemText: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    margin: 0,
    flex: 1,
    '& > span, & > a': {
      color: '#1D3066',
      fontSize: theme.spacing(1.7),
      fontWeight: 'inherit',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
    },
  },
  itemIcon: {
    justifyContent: 'center',
    width: theme.spacing(3.5),
    minWidth: 0,
    paddingRight: 0,
  },
  itemProjectIcon: {
    paddingLeft: 0,
  },
  itemOrganizationIcon: {
    marginLeft: theme.spacing(1),
  },
  centerText: {
    textAlign: 'center',
    marginTop: theme.spacing(0.525),
    marginBottom: theme.spacing(0.525),
    '& > a': {
      fontWeight: 400,
      color: '#5294FF',
      textDecoration: 'inherit',
    },
  },
  projectItem: {
    padding: theme.spacing(0.525, 1.4),
    marginBottom: theme.spacing(1.3),
    fontWeight: 400,
    color: 'inherit',
    '&:hover, &:focus': {
      backgroundColor: '#FAFAFA',
    },
  },
  lists: {
    maxHeight: '70vh',
    overflow: 'auto',
    width: theme.spacing(50),
    padding: theme.spacing(1, 0, 0, 0),
    '& > ul': {
      paddingBottom: 0,
      paddingTop: theme.spacing(1),
      '&:last-child': {
        marginBottom: theme.spacing(3.5),
      },
    },
  },
  subheader: {
    fontSize: theme.spacing(1.6),
    textTransform: 'uppercase',
    padding: theme.spacing(0, 1.925),
    marginBottom: theme.spacing(0.525),
    fontWeight: 'bold',
    color: '#808B9A',
    lineHeight: 'inherit',
  },
  createOrgBtn: {
    textAlign: 'center',
    padding: theme.spacing(1.575),
    '& > a': {
      fontWeight: 400,
      color: '#5294FF',
      textDecoration: 'inherit',
    },
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& > svg': {
      marginRight: theme.spacing(1),
    },
    '&:hover': {
      backgroundColor: '#F0F8FF',
    },
    boxShadow: '0px -4px 8px -3px rgba(120, 140, 179, 0.11)',
  },
  viewAllProjectsButton: {
    fontWeight: 400,
    color: '#5294FF',
    padding: 0,
    lineHeight: 'normal',
    verticalAlign: 'inherit',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
}));

interface OrganizationItemProps {
  organization: Organization;
  onSelectOrg: () => void;
  hasProject: boolean;
  isCurrentOrganization: boolean;
}

/**
 * Component for showing an organization item in the list.
 * It contains the organization's avatar, name, and a dropdown button
 *   which shows three most recently accessed projects of the organization.
 * The project items are part of this component too, they're indented slightly.
 * When the component is expanded, a request to fetch three most recently
 *   accessed projects will be sent to the server.
 */
const OrganizationItem = (props: OrganizationItemProps) => {
  const intl = useIntl();
  const classes = useStyles();
  const orgId = Number(useQuery().get('orgId'));
  const accountId = Number(useQuery().get('accountId'));
  const dispatch = useAppDispatch();
  const { config } = useConfig();
  const { flags } = useLaunchDarkly();
  const location = useLocation();
  const user = useSelector(fromAuth.selectUser);
  const { organization, hasProject, isCurrentOrganization, onSelectOrg } = props;
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { navigate, replaceQuery, replace, mergeQuery } = useNavigate();
  const accountPages = [
    accountHome.path,
    paymentMethod.path,
    accountSetting.path,
    accountSubscriptionManagement.path,
    testOpsPlatformCheckout.path,
    testOpsPlatformPaymentStatus.path,
    securitySettings.path,
    organizationManagement.path,
  ];
  const isAccountPage = accountPages
    .find(path => matchPath(path, location.pathname)?.pathname !== undefined);
  const isPaymentSuccessPage = useMatch(paymentSuccess.path)?.pathname !== undefined;
  const isTestCloudCheckout = useMatch(testCloudCheckout.path)?.pathname !== undefined;
  const isProjectManagement = useMatch(projectManagement.path)?.pathname !== undefined;
  const isProjectList = useMatch(projectList.path)?.pathname !== undefined;

  const activePlatformSubscription = useSelector(fromTestOpsPlatformSubscriptions
    .selectPaidTestOpsPlatformSubscriptionByAccountId(
      accountId,
    )).filter(sub => new Date(sub.expiryDate).getTime() > Date.now())?.[0];

  const hasTOEnterprise = useSelector(fromSubscriptions
    .selectHasTOEnterpriseByOrganizationId(Number(orgId) || 0));
  const recentProjects = useSelector(fromTestOpsProject
    .selectProjectsByCurrentUserAndOrgId(organization.id));

  const orgMigrationStatus = useSelector(
    fromOrganizations.selectOrgMigrationStatusById(organization.id),
  );

  const isSsoFlagEnabled = useSelector(fromOrganizationFeatureFlag.selectIsOrganizationSsoEnabled(
    Number(orgId),
  )) || false;

  const getRecentProjects = () => {
    setLoading(true);
    dispatch(fromTestOpsProject
      .doGetProjectsByUserEmailAndOrgId({
        userEmail: user!.email,
        organizationId: organization.id,
      }))
      .then(() => setLoading(false));
  };

  const hasEnableSSOAndCustomDomain = (
    activePlatformSubscription?.feature === OrganizationFeature.TESTOPS_PLATFORM
    && activePlatformSubscription.quota >= 30000)
    || hasTOEnterprise
    || isSsoFlagEnabled;
  const getSubdomainLink = (domain: string | undefined) => `https://${domain}.${process.env.REACT_APP_CUSTOM_DOMAIN_POSTFIX}`;

  useEffect(() => {
    if (expanded && !loading && !recentProjects && hasProject) {
      getRecentProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  useEffect(() => {
    if (orgMigrationStatus === undefined && flags?.organizationMigrationStatusEnabled) {
      dispatch(fromOrganizations.doGetOrgMigrationStatus({ id: organization.id }));
    }
  }, [orgMigrationStatus, flags]);

  const handleExpand = () => {
    setExpanded(true);
  };
  const handleCollapse = () => {
    setExpanded(false);
  };
  const handleSelectOrg = () => {
    onSelectOrg();
    if (isPaymentSuccessPage) {
      navigate(testCloudSubscriptionManagement.path, replaceQuery({ orgId: `${organization.id}` }));
      return;
    }
    if (isTestCloudCheckout) {
      replace(undefined, mergeQuery({ orgId: `${organization.id}` }));
      return;
    }
    if (isAccountPage) {
      replace(
        undefined,
        mergeQuery({ orgId: organization.id, accountId: organization.accountId }),
      );
      return;
    }
    if (isProjectManagement && !isProjectList) {
      goToProjectPage();
      return;
    }
    replace(undefined, replaceQuery({ orgId: organization.id }));
  };
  const goToProjectPage = () => {
    let projectUri = '';
    const oldUI = resolvePath(ssoPath, undefined, { redirect_uri: `${process.env.REACT_APP_ADMIN_URL}/organization/${organization.id}/teams/projects` });
    const newUI = resolvePath(projectManagement, undefined, { orgId: `${organization.id}` });
    const maintenanceUI = resolvePath(maintenanceProcess, undefined);
    if (orgMigrationStatus === OrganizationMigrationStatus.MIGRATED) {
      projectUri = newUI;
    } else if (flags?.projectStandardizeEnabled) {
      projectUri = maintenanceUI;
    } else if (flags?.projectManagementNewUIEnabled) {
      projectUri = newUI;
    } else {
      projectUri = oldUI;
    }
    window.location.href = projectUri;
  };
  const handleClickViewAllProjects = () => {
    onSelectOrg();
    if (orgId !== organization.id) {
      if (
        isProjectList
        && (orgMigrationStatus === OrganizationMigrationStatus.MIGRATED
          || (!flags?.projectStandardizeEnabled
            && flags?.projectManagementNewUIEnabled))
      ) {
        replace(undefined, replaceQuery({ orgId: organization.id }));
        return;
      }
      goToProjectPage();
      return;
    }
    if (!isProjectList) {
      goToProjectPage();
    }
  };

  const renderExpandOrCollapseButton = () => {
    if (!hasProject) {
      return null;
    }
    return (
      <div className={classes.chevron} onClick={expanded ? handleCollapse : handleExpand}>
        {expanded
          ? <KeyboardArrowUp width={12} height={12} />
          : <KeyboardArrowDown width={12} height={12} />}
      </div>
    );
  };

  return (
    <>
      <ListItem
        button
        className={classes.orgItem}
      >
        {isCurrentOrganization
          ? <div className={classes.selectedMarker} />
          : <div className={clsx(classes.selectedMarker, classes.invisible)} />}
        <ListItemIcon
          onClick={handleSelectOrg}
          className={clsx(classes.itemIcon, classes.itemOrganizationIcon)}
        >
          {organization.logo?.length
            ? (
              <img
                width={24}
                height={24}
                src={`/${organization.logo}`}
                title={organization.name}
                alt={organization.name}
              />
            ) : (
              <Avatar
                size="24"
                round
                name={organization.name}
                maxInitials={2}
              />
            )}
        </ListItemIcon>
        <ListItemText
          className={classes.itemText}
          onClick={handleSelectOrg}
        >
          {organization.name}
        </ListItemText>
        {renderExpandOrCollapseButton()}
      </ListItem>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <div>
          {loading && (
            <div className={classes.centerText}>{intl.formatMessage({ id: 'list_organizations_and_projects.loading_projects' })}</div>
          )}
          {recentProjects && (
            <List disablePadding>
              {recentProjects.map(proj => (
                <ListItem
                  button
                  className={classes.projectItem}
                  component={({ innerRef, ...props }) => (
                    <RouterLink
                      {...props}
                      to={organization?.domain && hasEnableSSOAndCustomDomain
                        ? resolvePath(ssoPath, undefined, { redirect_uri: `${getSubdomainLink(organization?.domain)}/team/${proj.teamId}/project/${proj.id}` })
                        : resolvePath(ssoPath, undefined, { redirect_uri: `${config?.testOpsPublicUrl}/team/${proj.teamId}/project/${proj.id}` })}
                    />
                  )}
                  underline="none"
                >
                  <div className={clsx(classes.itemIcon, classes.itemProjectIcon)} />
                  <ListItemIcon className={clsx(classes.itemIcon, classes.itemProjectIcon)}>
                    <Avatar
                      size="24"
                      round
                      name={proj.name}
                      maxInitials={2}
                    />
                  </ListItemIcon>
                  <ListItemText className={classes.itemText}>{proj.name}</ListItemText>
                </ListItem>
              ))}
            </List>
          )}
          {!loading && recentProjects?.length > 0 && (
            <div className={clsx(classes.centerText, classes.projectItem)}>
              <Button
                className={classes.viewAllProjectsButton}
                variant="text"
                onClick={handleClickViewAllProjects}
              >
                {intl.formatMessage({ id: 'list_organizations_and_projects.view_projects' })}
              </Button>
            </div>
          )}
        </div>
      </Collapse>
    </>
  );
};

/**
 * This component renders a list of organization items, each can be expanded
 *   to show a nested list of up to 3 most recent projects of that organization.
 * The component currently takes no props, and it is meant to uniform the
 *   top left navigation bar across TO, K1, and Admin.
 * It contains two groups: Current and All. The "Current" group contains only
 *   one organization, which is the current one.
 *   The "All" group contains all organizations, including the current one.
 */
const ListOrganizationsAndProjects = () => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const anchorEl = useRef<HTMLButtonElement>(null);
  const { config } = useConfig();
  const currentOrganization = useSelector(fromOrganizations.selectSelectedOrganization);
  const projectCountByOrgId = useSelector(fromTestOpsProject.selectProjectCount);
  const myOrganizations = useSelector(fromOrganizations.selectAllOrganizations);
  // Test result count will be fetched in layout if on-premise mode is on
  const testResultCount = useSelector(fromTestResultCount.selectLatestTestResultCount);
  const createNewOrganizationLink = resolvePath(ssoPath, undefined, { redirect_uri: `${process.env.REACT_APP_ADMIN_URL}/home/create-organization` });
  const intl = useIntl();
  const handleClick = () => {
    setOpen(prevOpen => !prevOpen);
  };
  // handle click away or click on menu item
  const handleClickAway = (event: any) => {
    if (anchorEl.current && anchorEl.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  /**
   * Check when data is loading, then not show component.
   */
  if (!currentOrganization) {
    return null;
  }

  return (
    <div>
      <Button
        className={classes.orgList}
        ref={anchorEl}
        endIcon={<ArrowDownIcon />}
        onClick={handleClick}
      >
        <span className={classes.orgName}>{currentOrganization.name}</span>
      </Button>
      <Popper
        open={open}
        anchorEl={anchorEl.current}
        role={undefined}
        placement="bottom-start"
        disablePortal
        transition
        modifiers={[
          { name: 'preventOverflow', enabled: false },
          { name: 'flip', enabled: false },
        ]}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: placement === 'bottom-start' ? 'top left' : 'center bottom' }}
          >
            <Paper elevation={1}>
              <ClickAwayListener onClickAway={handleClickAway}>
                <div>
                  <div className={classes.lists}>
                    {config?.onpremise ? (
                      <List>
                        <OrganizationItem
                          onSelectOrg={() => setOpen(false)}
                          organization={currentOrganization}
                          isCurrentOrganization
                          hasProject={testResultCount?.usagePlan !== UsagePlan.COMMUNITY
                            ? projectCountByOrgId[currentOrganization.id] > 0 : false}
                        />
                      </List>
                    ) : (
                      <>
                        <List
                          subheader={<ListSubheader disableSticky className={classes.subheader}>{intl.formatMessage({ id: 'list_organizations_and_projects.current' })}</ListSubheader>}
                        >
                          <OrganizationItem
                            onSelectOrg={() => setOpen(false)}
                            organization={currentOrganization}
                            isCurrentOrganization
                            hasProject={projectCountByOrgId[currentOrganization.id] > 0}
                          />
                        </List>
                        <List
                          subheader={<ListSubheader disableSticky className={classes.subheader}>{intl.formatMessage({ id: 'list_organizations_and_projects.all' })}</ListSubheader>}
                        >
                          {sortBy(['createdAt'], myOrganizations).map(organization => (
                            <OrganizationItem
                              onSelectOrg={() => setOpen(false)}
                              organization={organization}
                              isCurrentOrganization={false}
                              hasProject={projectCountByOrgId[organization.id] > 0}
                            />
                          ))}
                        </List>
                      </>
                    )}
                  </div>
                  {!config?.onpremise && (
                    <div className={classes.createOrgBtn}>
                      <AddOrganizationIcon />
                      <Link href={createNewOrganizationLink} underline="none">
                        {intl.formatMessage({ id: 'list_organizations_and_projects.create_new_organization' })}
                      </Link>
                    </div>
                  )}
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};

export default ListOrganizationsAndProjects;
